/**
 * useJobsData Hook
 *
 * Manages scraping jobs data fetching, filtering, and pagination.
 * Separates jobs data logic from the main PropertyDiscoveryPage component.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  collection,
  query,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { db, reconnectFirestore } from "../firebase";
import { useToast } from "../components/shared/Toast";
import { useViewMode } from "./useViewMode";

const MAX_RETRIES = 3;
const RETRY_BASE_MS = 2000;

export const useJobsData = () => {
  const toast = useToast();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [filter, setFilter] = useState("all");
  const [viewMode, setViewMode] = useViewMode("jobsViewMode");
  const [perPage, setPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [listenerError, setListenerError] = useState(null);
  // Set when a reconnect itself could not complete: the client is blocked,
  // not merely disconnected, and only a reload clears it.
  const [reconnectError, setReconnectError] = useState(null);
  // Bumped to throw the listener away and start a fresh one.
  const [generation, setGeneration] = useState(0);
  const reconnecting = useRef(false);

  // Stats derived from jobs
  const stats = {
    totalJobs: jobs.length,
    pending: jobs.filter((j) => j.status === "pending").length,
    running: jobs.filter((j) => j.status === "running").length,
    completed: jobs.filter((j) => j.status === "completed").length,
    failed: jobs.filter((j) => j.status === "failed").length,
  };

  // Archive a job (soft delete)
  const archiveJob = useCallback(
    async (jobId, e) => {
      e?.preventDefault();
      e?.stopPropagation();

      if (
        !window.confirm(
          "Archive this job?\n\n" +
            "This will hide the job from the list but preserve all data. " +
            "Properties scraped by this job will remain in the database with their link to this job intact.\n\n" +
            "This action can be reversed by an admin if needed."
        )
      ) {
        return;
      }

      try {
        setDeleting(jobId);
        await updateDoc(doc(db, "discovery_scrapes", jobId), {
          archived: true,
          archivedAt: serverTimestamp(),
        });
        setJobs((prev) => prev.filter((job) => job.id !== jobId));
        toast.success("Job archived successfully");
      } catch (error) {
        console.error("Error archiving job:", error);
        if (error.code === "permission-denied") {
          toast.error("Permission denied: Only admins can archive jobs.");
        } else {
          toast.error("Failed to archive job: " + error.message);
        }
      } finally {
        setDeleting(null);
      }
    },
    [toast]
  );

  // Delete a job (hard delete - kept for backward compatibility but renamed internally)
  const deleteJob = archiveJob;

  // Filter jobs
  const filteredJobs =
    filter === "all" ? jobs : jobs.filter((job) => job.status === filter);

  // Pagination
  const totalPages = Math.ceil(filteredJobs.length / perPage);
  const paginatedJobs = filteredJobs.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  // Reset page when filter or perPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, perPage]);

  /**
   * Real-time listener — self-healing.
   *
   * ⚠️ A Firestore listener that errors is DEAD: it never resumes by itself.
   * This one used to show a five-second toast and then sit there, so the
   * Scrapes tab stopped receiving scrapes until someone reloaded the page
   * (10 Sep 2026: a new scrape did not appear, and the tab sometimes read
   * "No scrapes yet" with 51 scrapes on file). It now resubscribes with a
   * back-off, and reports failure only once retrying has not worked.
   *
   * Subscribed synchronously, so there is no window in which an unmount can
   * run before the unsubscribe exists (the leak the old async setup guarded).
   */
  useEffect(() => {
    let unsubscribe = null;
    let retryTimer = null;
    let cancelled = false;
    let attempts = 0;
    const jobsQuery = query(
      collection(db, "discovery_scrapes"),
      orderBy("createdAt", "desc")
    );

    const subscribe = () => {
      unsubscribe = onSnapshot(
        jobsQuery,
        (jobsSnapshot) => {
          attempts = 0;
          // Use the denormalised counts already stored on the scrape doc
          // (successfulProperties / totalProperties). Previously this ran a
          // getCountFromServer query PER job inside the snapshot callback — an
          // N+1 that re-fired on every change and made the Jobs tab slow.
          const jobsData = jobsSnapshot.docs
            .filter((d) => !d.data().archived)
            .map((d) => {
              const job = { id: d.id, ...d.data() };
              return {
                ...job,
                propertiesFound:
                  job.successfulProperties ?? job.totalProperties ?? 0,
              };
            });
          setJobs(jobsData);
          setListenerError(null);
          setLoading(false);
        },
        (error) => {
          console.error("Error fetching jobs:", error);
          unsubscribe = null;
          if (cancelled) return;
          if (attempts < MAX_RETRIES) {
            attempts += 1;
            retryTimer = setTimeout(subscribe, RETRY_BASE_MS * attempts);
            return;
          }
          setListenerError(error);
          setLoading(false);
          toast.error("Scrapes stopped updating. Reconnect or reload to try again.", {
            debugInfo: {
              operation: "useJobsData onSnapshot(discovery_scrapes)",
              params: { attempts },
              error: error.stack || error.toString(),
            },
          });
        }
      );
    };

    subscribe();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      if (unsubscribe) unsubscribe();
    };
  }, [toast, generation]);

  /**
   * Restart the connection and the listener, without a page reload. For when
   * the stream has gone quiet rather than failed — which raises no error, so
   * nothing above can notice it on its own.
   */
  const reconnect = useCallback(async () => {
    if (reconnecting.current) return;
    reconnecting.current = true;
    try {
      await reconnectFirestore();
      setReconnectError(null);
    } catch (e) {
      console.warn("[useJobsData] reconnect failed:", e);
      setReconnectError(e.message || String(e));
    } finally {
      reconnecting.current = false;
      setGeneration((g) => g + 1);
    }
  }, []);

  return {
    jobs: paginatedJobs,
    allJobs: jobs,
    loading,
    deleting,
    filter,
    setFilter,
    viewMode,
    setViewMode,
    perPage,
    setPerPage,
    currentPage,
    setCurrentPage,
    totalPages,
    stats,
    deleteJob,
    filteredJobs,
    listenerError,
    reconnectError,
    reconnect,
  };
};
